import { useEffect, useMemo, useRef, useState } from "react";
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Navbar } from "../components/Layout/Navbar";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useCookies } from "react-cookie";
import Footer from "../components/Auth/Footer";
import CategoryDropdown from "../components/SiteSettings/CategoryDropdown";
import ColorPalletsList from "../components/Admin/ColorPalletsList";
import TemplateCategoryList from "../components/Admin/TemplateCategoryList";
import PublishableTemplatesList from "../components/Admin/PublishableTemplatesList";
import UserAcquisitionLogs from "../components/Admin/UserAcquisitionLogs";
import SitesList from "../components/Admin/SitesList";
import AITemplates from "../components/Admin/AITemplates";
import UserManagement from "../components/Admin/UserManagement";
import axiosInstance from "../axiosInstance";
import { enqueueSnackbar } from "notistack";
import {
  extractPublishStatusPayload,
  normalizePublishStatus,
} from "../utils/publishStatus";

const NAV_ITEMS = [
  { key: "colorPallets", label: "Color Pallets" },
  { key: "templateCategory", label: "Template Category" },
  { key: "publishTemplates", label: "Publish Templates" },
  { key: "userLogs", label: "User Acquisition Logs" },
  { key: "allSites", label: "Sites List" },
  { key: "aiTemplates", label: "AI Templates" },
  { key: "userManagement", label: "User Management" },
];

const AdminPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [cookies] = useCookies(["rp_user_data"]);
  const location = useLocation();
  const stateData = location.state;

  // Marketing site id (fixed)
  const marketingSiteId = "2e2b85c5-1b76-4e74-8e39-c82749386c53";

  // Publish status for smart button
  const [publishInfo, setPublishInfo] = useState({
    status: "Unknown",
    stuck: false,
    publishBatchStart: 0,
  });

  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const l_menu = query.get("menu");
  const activeMenu = l_menu || "colorPallets";

  const activeLabel = NAV_ITEMS.find((item) => item.key === activeMenu)?.label || "";

  // Polling interval ref so we can stop polling when publish is terminal
  const pollRef = useRef<number | null>(null);

  const fetchPublishInfo = async () => {
    try {
      const resp = await axiosInstance.get("/api/configs/check-publish-status", {
        params: { siteId: marketingSiteId },
      });
      const statusPayload = extractPublishStatusPayload(resp);

      setPublishInfo({
        status:
          normalizePublishStatus(
            statusPayload?.status ??
              statusPayload?.publishStatus ??
              statusPayload?.pageStatus ??
              statusPayload?.publishingStatus
          ) || "unknown",
        stuck: !!statusPayload?.stuck,
        publishBatchStart: Number(statusPayload?.publishBatchStart || 0),
      });
    } catch (e) {
      // Best effort. Don't spam errors in admin UI.
      // Keep previous publishInfo so button doesn't flicker.
    }
  };

  useEffect(() => {
    fetchPublishInfo();

    // Start polling
    pollRef.current = window.setInterval(fetchPublishInfo, 15000);

    return () => {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    // Stop polling once publish reaches a terminal state
    const terminal =
      publishInfo.status === "completed" || publishInfo.status === "failed";

    if (terminal && pollRef.current !== null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [publishInfo.status]);

  const handleMarketingSiteRepublish = async () => {
    setIsLoading(true);
    try {
      // Refresh current status just before action
      const statusRes = await axiosInstance.get("/api/configs/check-publish-status", {
        params: { siteId: marketingSiteId },
      });
      const statusPayload = extractPublishStatusPayload(statusRes);
      const status = normalizePublishStatus(
        statusPayload?.status ??
          statusPayload?.publishStatus ??
          statusPayload?.pageStatus ??
          statusPayload?.publishingStatus
      );
      const stuck = !!statusPayload?.stuck;
      const publishBatchStart = Number(statusPayload?.publishBatchStart || 0);

      // If stuck -> resume
      if (status === "running" && stuck) {
        // Resume continues from the server's last saved batch/progress (publishBatchStart from status API),
        // not from any client-side split/halve calculation.
        await axiosInstance.post("/api/configs/publish/resume", {
          siteId: marketingSiteId,
        });

        enqueueSnackbar(
          `Publish was stuck. Resuming from batch ${Number(publishBatchStart || 0)}…`,
          { variant: "warning" }
        );

        fetchPublishInfo();
        return;
      }

      // If running (fresh) -> do nothing
      if (status === "running") {
        enqueueSnackbar("Publish is already running…", { variant: "info" });
        return;
      }

      // Otherwise -> start publish
      const res = await axiosInstance.post("/api/configs/publish", {
        siteId: marketingSiteId,
        publishAllPages: true,
      });

      if (res.status === 200) {
        enqueueSnackbar("Publishing in progress!", { variant: "warning" });
        fetchPublishInfo();
      } else {
        enqueueSnackbar("Could not start publish.", { variant: "error" });
      }
    } catch (error) {
      console.error("Marketing republish/resume error", error);
      enqueueSnackbar("Something went wrong while publishing.", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const marketingButtonText =
    publishInfo.status === "running" && publishInfo.stuck
      ? `Resume Marketing Publish (batch ${publishInfo.publishBatchStart})`
      : publishInfo.status === "running"
      ? "Publishing…"
      : "Republish Marketing Site";

  const marketingButtonDisabled =
    publishInfo.status === "running" && !publishInfo.stuck;

  const handleCategoryChange = (value: string) => {
    const item = NAV_ITEMS.find((i) => i.label === value);
    if (item) {
      navigate(`/admin?menu=${item.key}`);
    }
  };

  return (
    <>
      <Helmet>
        <title>RocketPages - Admin Panel</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="font-body text-base font-medium">
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <Navbar
          {...stateData}
          title={`Admin Panel${activeLabel ? ` | ${activeLabel}` : ""}`}
        />

        {/* Desktop sidebar */}
        <div className="bg-gray-100 flex-row fixed h-screen w-48 top-[64px] hidden md:flex">
          <div className="left-navbar-tabs w-full h-full bg-catelina-blue text-white flex flex-col py-2">
            {NAV_ITEMS.map((item) => (
              <span
                className={`px-6 hover:cursor-pointer py-2 left-sidebar-tab-${item.key} ${
                  activeMenu === item.key
                    ? "bg-white-smoke text-catelina-blue"
                    : ""
                }`}
                key={item.key}
                onClick={() => navigate(`/admin?menu=${item.key}`)}
              >
                {item.label}
              </span>
            ))}
            <div className="mt-auto px-3 py-4">
              <button
                className={`w-full text-sm px-3 py-2 rounded ${
                  marketingButtonDisabled
                    ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                    : "bg-white text-catelina-blue hover:bg-gray-100 cursor-pointer"
                }`}
                disabled={marketingButtonDisabled}
                onClick={handleMarketingSiteRepublish}
              >
                {marketingButtonText}
              </button>
            </div>
          </div>
        </div>

        {/* Content area */}
        <div className="left-navbar-tab-contents min-h-screen w-full my-4 px-4 md:pl-[280px] pb-10 pt-[64px] bg-white-smoke flex flex-col overflow-x-auto">
          {/* Mobile dropdown + republish button */}
          <div className="flex md:hidden py-2 items-center gap-3 flex-wrap">
            <CategoryDropdown
              items={NAV_ITEMS.map((i) => i.label)}
              handleChange={handleCategoryChange}
              category={activeLabel}
            />
            <button
              className={`text-sm px-3 py-2 rounded whitespace-nowrap ${
                marketingButtonDisabled
                  ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                  : "bg-catelina-blue text-white hover:bg-opacity-90 cursor-pointer"
              }`}
              disabled={marketingButtonDisabled}
              onClick={handleMarketingSiteRepublish}
            >
              {marketingButtonText}
            </button>
          </div>

          {activeMenu === "colorPallets" && <ColorPalletsList />}
          {activeMenu === "templateCategory" && <TemplateCategoryList />}
          {activeMenu === "publishTemplates" && <PublishableTemplatesList />}
          {activeMenu === "userLogs" && <UserAcquisitionLogs />}
          {activeMenu === "allSites" && <SitesList />}
          {activeMenu === "aiTemplates" && <AITemplates />}
          {activeMenu === "userManagement" && <UserManagement />}
        </div>
      </div>

      <div className="w-full">
        <Footer />
      </div>
    </>
  );
};

export default AdminPage;
